import {
  isJsonDataCreatorResource,
  parseJsonDataCreator,
  resolveJsonDataCreatorAvatar,
} from '@/utils/core/data-transformation/jsonDataCreatorHelpers';

/** Audit row config — supports legacy (userCreator) and channel/billing (creator) field names. */
export const HERO_DETAIL_AUDIT_ROWS = [
  {
    labelId: 'config.info.createdBy',
    dateOnlyLabelId: 'config.info.creationDate',
    userKeys: ['userCreator', 'creator'],
    dateKey: 'created',
    hideWhenEmptyUser: false,
  },
  {
    labelId: 'config.info.updatedBy',
    userKeys: ['userModifier', 'modifier'],
    dateKey: 'updated',
    hideWhenEmptyUser: true,
  },
];

/** Flat list of audit field names for form/skeleton helpers. */
export const HERO_DETAIL_AUDIT_FIELD_NAMES = HERO_DETAIL_AUDIT_ROWS.flatMap(
  ({ userKeys, dateKey }) => [...userKeys, dateKey],
);

const auditNameSet = new Set(HERO_DETAIL_AUDIT_FIELD_NAMES);

const MODIFIER_FIELD_NAMES = ['userModifier', 'modifier'];

const mapFieldToSkeletonRow = (field) => {
  if (field.type === 'switch') return 'switch';
  if (field.type === 'photo' || field.type === 'user-photo') return 'photo';
  // Optional cover/media rows hide when empty in HeroDetails — skip so the
  // skeleton matches the usual details card (avatar + text + switch).
  if (field.type === 'upload-dragger-image') return null;
  return 'default';
};

/** Edit-form skeleton variant for a single `_form.js` field. */
export const getConfigFormSkeletonVariant = (field) => {
  switch (field?.type) {
    case 'switch':
      return 'switch';
    case 'colorpicker':
      return 'colorpicker';
    case 'textarea':
      return 'textarea';
    case 'select':
      return 'select';
    case 'photo':
      return 'photo';
    case 'radio-group':
      return 'radio-group';
    case 'upload-dragger':
      return field.draggerVariant === 'embedded' ? 'upload-embedded' : 'upload';
    case 'upload-dragger-image':
      return field.draggerVariant === 'embedded' ? 'upload-embedded' : 'upload-image';
    case 'vectors-uploader':
      return 'upload';
    default:
      return 'text';
  }
};

/** Visible non-audit fields for ConfigFormDrawer edit skeleton. */
export const getConfigFormSkeletonFields = (fields = []) =>
  fields.filter((field) => !field.hidden && !auditNameSet.has(field.name));

const hasVisibleFormField = (fields, name) =>
  fields.some((field) => field.name === name && !field.hidden);

const pickFirstDefined = (source, keys) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value != null && value !== '') return value;
  }
  return null;
};

/**
 * Resolves creator/updater for HeroDetailsAudit using jsonDataCreatorHelpers
 * (same as channel/billing listados and DataCreator).
 */
export const resolveHeroDetailAuditUser = (sourceItem, userKeys, fallbackValue) => {
  const raw = pickFirstDefined(sourceItem, userKeys) ?? fallbackValue;

  if (raw == null || raw === '') {
    return { fullName: null, id: null, dataCreator: null };
  }

  const resource = parseJsonDataCreator(raw) || (isJsonDataCreatorResource(raw) ? raw : null);

  if (resource) {
    const { name, id } = resolveJsonDataCreatorAvatar(raw);
    return {
      fullName: name,
      id,
      dataCreator: resource,
    };
  }

  if (typeof raw === 'object') {
    return {
      fullName: raw.fullName || raw.displayName || raw.name || raw.resourceDisplay || null,
      id: raw.id || raw.resourceIdentifier || null,
      dataCreator: null,
    };
  }

  return {
    fullName: typeof raw === 'string' ? raw : null,
    id: null,
    dataCreator: null,
  };
};

/**
 * Derives SkeletonHeroDetails row variants from a `_form.js` fields array.
 * Excludes hidden fields and audit metadata (same split as splitHeroDetailFields).
 * By default prepends an ID row like getDetails does when the form has no visible id field.
 */
export const getHeroDetailSkeletonRows = (
  fields = [],
  { showId = true, idPosition = 0, includeAudit = false, leadingPhoto = false } = {},
) => {
  const rows = fields
    .filter((field) => !field.hidden && (includeAudit || !auditNameSet.has(field.name)))
    .map(mapFieldToSkeletonRow)
    .filter(Boolean);

  // Details views that inject an avatar in transformDetails (Users) have no photo
  // field in `_form.js` — prepend the circle so the skeleton matches the card.
  if (leadingPhoto && !rows.includes('photo')) {
    rows.unshift('photo');
  }

  const photoCount = rows.filter((variant) => variant === 'photo').length;

  if (showId && !hasVisibleFormField(fields, 'id')) {
    // Keep ID after a leading avatar, same order as HeroDetails photo + id.
    const position = Math.max(photoCount, Math.min(idPosition + photoCount, rows.length));
    rows.splice(position, 0, 'default');
  }

  return rows;
};

/**
 * Row variants for ConfigFormDrawer edit skeleton (no synthetic ID row; add mode has no skeleton).
 */
export const getConfigFormSkeletonRows = (fields = []) =>
  getConfigFormSkeletonFields(fields).map(getConfigFormSkeletonVariant);

/**
 * Audit KPI count for SkeletonHeroDetailsAudit based on form fields.
 * Returns 0 when the form has no audit fields, 1 without modifier, 2 otherwise.
 */
export const getHeroDetailSkeletonAuditCardCount = (fields = []) => {
  const hasAudit = fields.some((field) => auditNameSet.has(field.name));
  if (!hasAudit) return 0;
  return fields.some((field) => MODIFIER_FIELD_NAMES.includes(field.name)) ? 2 : 1;
};

/**
 * Empty for detail visibility: null / undefined / ''.
 * Keep `0` and `false` — backends often omit default enum `0` or `false`, and when present
 * they must still render (same idea as getEnumTranslationKey treating nil as 0).
 */
export const isEmptyDetailValue = (value) => value === null || value === undefined || value === '';

export const isVisibleDetailField = (item, showNull = false) =>
  !isEmptyDetailValue(item?.value) ||
  showNull ||
  item?.showNull ||
  (item?.viewComponent === 'iframe-url' && Boolean(item?.viewProps?.viewUrl)) ||
  (item?.viewComponent === 'file' &&
    Boolean(item?.viewProps?.viewUrl || item?.viewProps?.downloadUrl));

const pickAuditField = (byName, keys) => keys.map((key) => byName[key]).find(Boolean);

/**
 * Splits getDetails rows into main content and audit metadata.
 * Audit order: creator → created → updater → updated.
 */
export const splitHeroDetailFields = (data, showNull = false) => {
  const visible = (data || []).filter((item) => isVisibleDetailField(item, showNull));
  const byName = Object.fromEntries(visible.map((item) => [item.name, item]));

  const auditFields = HERO_DETAIL_AUDIT_ROWS.flatMap(({ userKeys, dateKey }) => {
    const entries = [];
    const userField = pickAuditField(byName, userKeys);
    if (userField) entries.push(userField);
    if (byName[dateKey]) entries.push(byName[dateKey]);
    return entries;
  });

  // `detail: true` + `hidden: true` → show in details, keep out of the edit form body.
  const mainFields = visible.filter(
    (item) => !auditNameSet.has(item.name) && (!item.hidden || item.detail),
  );

  return { mainFields, auditFields };
};
